import { Map } from "immutable";
import { isNil } from "lodash";

import { immutableRecords } from "../../../types";
import { calcEventInternalLayout } from "./calcEventInternalLayout";
import { expandLayoutSecondPass } from "./expandLayoutSecondPass";
import { getLayoutConstants } from "./getLayoutConstants";

const updateMostRightPerLine = (mostRightPerLine, lineIndex, itemHeightInLines, itemRight) => {
  let result = mostRightPerLine.slice();
  const lastLine = lineIndex + itemHeightInLines;
  const lastExistingLine = Math.min(result.length, lastLine);
  for (let i = lineIndex; i < lastExistingLine; i++) {
    result[i] = itemRight;
  }
  for (let i = lastExistingLine; i < lastLine; i++) {
    result.push(itemRight);
  }
  return result;
};

const _itemPositionForIndex = (eventsToLayout, positions, index) => positions[eventsToLayout.get(index)];

/**
 * The Compact layout places an event at the highest point that it can fit in its entirety
 * This needs to be used within a two-pass algorithm, since expanded events need to expand
 * from their preferred position, rather than change position as a result of being expanded.
 *
 * This algorithm works by tracking the rightMost position at every vertical line in our layout
 * To work out where to place a node we first calculate how many lines tall it is
 * And then look for a slot where it can fit in every line it needs
 * @param {string} viewId
 * @param {List} sortedEventIds
 * @param {Map} eventsById
 * @param {Set} ongoingDescendantsSet
 * @param {*} layoutIds
 * @param {*} expandedItemsSet
 * @param {*} unitScale
 * @param {*} startY
 * @param {*} fontSize
 * @param {*} dateForNow
 */
const _calcWaterfallLayoutForEventList = (
  viewId,
  sortedEventIds,
  eventsById,
  ongoingDescendantsSet,
  layoutIds,
  expandedItemsSet,
  unitScale,
  startY,
  fontSize,
  dateForNow,
  showItemCard
) => {
  const layoutConstants = getLayoutConstants(fontSize, showItemCard);

  let lastBottom = null,
    lastBottomRow = null,
    mostLeft = null,
    mostRight = null,
    mostBottom = startY;

  let mostRightPerLine = [];

  let positions = {};

  const layoutIdsSet = layoutIds.toSet();

  const eventsToLayout = sortedEventIds.filter(id => layoutIdsSet.has(id));
  const eventCount = eventsToLayout.size;

  let nextPossibleTopIndex = 0;

  // First of all, we need to calculate the internal layout of all events so that we know their size
  eventsToLayout.forEach(evId => {
    const ev = eventsById.get(evId);
    const hasOngoingDescendants = ongoingDescendantsSet.has(ev.id);
    positions[evId] = calcEventInternalLayout(
      viewId,
      ev,
      unitScale,
      fontSize,
      dateForNow,
      hasOngoingDescendants,
      showItemCard
    );
  });

  // Helper function to avoid passing as many variables around
  const positionForIndex = index => _itemPositionForIndex(eventsToLayout, positions, index);

  eventsToLayout.forEach((evId, index) => {
    const ev = eventsById.get(evId);
    let position = positions[evId];

    // And therefore what its height is
    let heightInLines = Math.ceil(position.totalRect.height / layoutConstants.LAYOUT_LINE_HEIGHT);

    // See if we can even try to place it at the top, or if we know it will fail we might as well skip ahead
    let placeAtTop = index >= nextPossibleTopIndex;

    // It may potentially fit at the top, so let's follow that through
    if (placeAtTop) {
      // Investigate whether it can be placed at the top
      // To do this, we need to check this and all subsequent nodes until we reach
      // clear space at the bottom with no offsets to contend with
      let testLine = 0;
      for (let z = index; z < eventCount; z++) {
        const zPosition = positionForIndex(z);
        const zLineHeight = Math.ceil(zPosition.totalRect.height / layoutConstants.LAYOUT_LINE_HEIGHT);
        // Assuming we place it in this chain, what would be the top and bottom rows for this item?
        const topRow = testLine;
        const bottomRow = topRow + zLineHeight - 1;

        // If the top row is clear of our existing rows, then we know we are home free and can let
        // the main evId be placed at the top
        if (topRow > mostRightPerLine.length) {
          break;
        }

        // Otherwise, we need to check if we can fit into each line
        for (let row = topRow; row <= bottomRow; row++) {
          if (row >= mostRightPerLine.length) {
            break;
          }
          if (zPosition.totalRect.left < mostRightPerLine[row]) {
            // Item Z clashes at this row, so we know the outer item evId cannot go at the top
            placeAtTop = false;

            // To save some time, we can now work out the earliest item that could be placed
            // on top. We want to:
            // a) Find the first item with a left value clear of that collision node
            // b) Work backwards from that node to the top, by taking into account the row heights of
            //    each previous item.
            // c) Do not let something that is the same x-position as our current top-level item from
            //    going to the top though, because that creates a weird situation where we are splitting
            //    identical items.

            // Step 1: find the first item clear of this item
            nextPossibleTopIndex = eventCount;
            for (let a = z + 1; a < eventCount; a++) {
              const aPosition = positionForIndex(a);
              if (aPosition.totalRect.left > mostRightPerLine[row]) {
                nextPossibleTopIndex = a;
                break;
              }
            }

            // Step 2: Work backwards to find which item would go at the top if we were to put this item
            // at this particular row
            let aRow = row;
            while (nextPossibleTopIndex > 0 && aRow > 0) {
              nextPossibleTopIndex--;
              const nPosition = positionForIndex(nextPossibleTopIndex);
              const nLineHeight = Math.ceil(nPosition.totalRect.height / layoutConstants.LAYOUT_LINE_HEIGHT);
              aRow -= nLineHeight;
              if (nPosition.totalRect.left <= position.totalRect.left) {
                nextPossibleTopIndex++;
                aRow = 0;
                break;
              }
            }
            // If we have gone below 0, we need to add 1
            if (aRow < 0) {
              nextPossibleTopIndex++;
            }

            break;
          }
        }

        // If we have already failed, then bail
        if (!placeAtTop) {
          break;
        } else {
          // Otherwise, we need to test the placement of the next item
          testLine += zLineHeight;
        }
      }
    }

    // By now a decision has been made, so lets put it where it goes
    const linePlacement = placeAtTop ? 0 : lastBottomRow; // + 1;
    const top = startY + linePlacement * layoutConstants.LAYOUT_LINE_HEIGHT;
    lastBottom = top + position.totalRect.height;

    positions[ev.id] = position.setIn(["totalRect", "top"], top).setIn(["totalRect", "bottom"], lastBottom);

    // Track where we placed this item so that it becomes the start position for any children
    lastBottomRow = Math.ceil((lastBottom - startY) / layoutConstants.LAYOUT_LINE_HEIGHT);

    // Update our offsets for the next iteration
    mostRightPerLine = updateMostRightPerLine(mostRightPerLine, linePlacement, heightInLines, position.totalRect.right);

    // Extend when the current event exceeding the most range
    if (mostLeft === null || position.totalRect.left < mostLeft) {
      mostLeft = position.totalRect.left;
    }
    if (mostRight === null || position.totalRect.right > mostRight) {
      mostRight = Math.round(position.totalRect.right);
    }
    if (lastBottom > mostBottom) {
      mostBottom = lastBottom;
    }
  });

  if (mostLeft === null) mostLeft = 0;
  if (mostRight === null) mostRight = 0;

  return {
    positions,
    bounds: immutableRecords.FlatRectRecord({
      left: mostLeft,
      right: mostRight,
      top: startY,
      bottom: mostBottom,
      width: mostRight - mostLeft,
      height: mostBottom - startY
    })
  };
};

const _calcNodeLayoutWaterfall = (
  viewId,
  sortedEventIds,
  eventsById,
  ongoingDescendantsSet,
  thisLevelItems,
  expandedItemsSet,
  unitScale,
  startY,
  fontSize,
  dateForNow,
  showItemCard
) => {
  let result = _calcWaterfallLayoutForEventList(
    viewId,
    sortedEventIds,
    eventsById,
    ongoingDescendantsSet,
    thisLevelItems,
    expandedItemsSet,
    unitScale,
    startY,
    fontSize,
    dateForNow,
    showItemCard
  );
  result = expandLayoutSecondPass(
    viewId,
    result,
    _calcNodeLayoutWaterfall,
    sortedEventIds,
    eventsById,
    ongoingDescendantsSet,
    thisLevelItems,
    expandedItemsSet,
    unitScale,
    startY,
    fontSize,
    dateForNow,
    showItemCard
  );
  return result;
};

export const calcNodeLayoutWaterfall = (
  viewId,
  data,
  ongoingDescendantsSet,
  topLevelItems,
  expandedItems,
  unitScale,
  fontSize,
  dateForNow,
  showItemCard
) => {
  const expandedItemsSet = expandedItems.toSet();
  const layoutConstants = getLayoutConstants(fontSize, showItemCard);
  const topLevelSet = topLevelItems.toSet();

  const top = 0;
  let groupTop = 0;

  let minLeft = null;
  let maxRight = null;

  const groups = data.groups.map(group => {
    const visibleTopLevelItems = group.eventIds.filter(id => topLevelSet.has(id));

    const thisGroupTop = groupTop;

    if (group.showHeader) {
      groupTop += layoutConstants.GROUP_HEADER_HEIGHT;
    }
    groupTop += layoutConstants.GROUP_TOP_PADDING;

    const result = _calcNodeLayoutWaterfall(
      viewId,
      group.eventIds,
      data.eventsById,
      ongoingDescendantsSet,
      visibleTopLevelItems,
      expandedItemsSet,
      unitScale,
      groupTop,
      fontSize,
      dateForNow,
      showItemCard
    );

    if (result.bounds.width > 0) {
      if (isNil(minLeft) || result.bounds.left < minLeft) {
        minLeft = result.bounds.left;
      }
      if (isNil(maxRight) || result.bounds.right > maxRight) {
        maxRight = result.bounds.right;
      }
    }

    const groupBottom = result.bounds.bottom + layoutConstants.TIMELINE_BOTTOM_PADDING;

    const groupBounds = result.bounds.merge({
      top: thisGroupTop,
      bottom: groupBottom,
      height: groupBottom - thisGroupTop
    });

    groupTop = groupBottom;

    return group.merge({
      bounds: groupBounds,
      positions: Map(result.positions),
      headerHeight: layoutConstants.GROUP_HEADER_HEIGHT
    });
  });

  const bounds = immutableRecords.FlatRectRecord({
    top,
    bottom: groupTop,
    height: groupTop,
    left: minLeft,
    right: maxRight,
    width: !isNil(minLeft) ? maxRight - minLeft : 0
  });

  return data.merge({
    groups,
    unitScale,
    bounds
  });
};
